import { useRef, useEffect, useState } from "react"
import {
  PrismButton,
  PrismCheckbox,
  PrismDialog,
  PrismInputText,
  PrismSelect,
  PrismTypeahead,
} from "@prism-ui/react"
import useFetch from "../../../../hooks/useFetch"
import MessageEditor from "../../../MessageEditor/MessageEditor.tsx"
import { EmailReminder } from "../../../../schema"
import { UrlOption } from "./types"
import axios from "axios"
import './EmailModal.css'
import classNames from "../../../../util/classNames"
import DateTimePicker from "../../../DateTimePicker/DateTimePicker"
import AddUrlModal from './AddUrlModal'


const defaultFrom = 'clear@comcast.com'

const frequencyOptions = [
  { value: 'daily', display: 'Daily (including weekends)' },
  { value: 'business_daily', display: 'Business Days Only (Mon-Fri)' },
  { value: 'weekly', display: 'Weekly' },
  { value: 'monthly', display: 'Monthly' },
]

const frequencyLabels: Record<string, string> = {
  daily: 'Each Day at',
  business_daily: 'Each Business Day at',
  weekly: 'Each Week at',
  monthly: 'Each Month at',
}

const isEmptyHtml = (s:string|undefined) => {
  if (!s) return true
  return !![/^<p><br><\/p>$/, /^<p> *<\/p>$/].find(re => re.test(s.trim() as string))
}

interface Option {
  value: string
  label: string
}

interface FormErrors {
  sendOn?: string
  endDate?: string
  recipients?: string
  chapters?: string
  subject?: string
  htmlBody?: string
}

interface Props {
  show: boolean
  onHide: (wasSuccessful?: boolean, action?: 'created' | 'updated')=>void
  editId?: number
  recipientOptions: Option[]
  chapterOptions: Option[]
  urlOptions: UrlOption[]
  onUrlCreated: () => void
  onDelete?: (id: number) => void
}

const EmailModal = ({show, onHide, editId, recipientOptions, chapterOptions, urlOptions, onUrlCreated, onDelete}:Props) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>(null)
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const tomorrow9AM = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    tomorrow.setHours(9)
    tomorrow.setMinutes(0)
    tomorrow.setSeconds(0)
    tomorrow.setMilliseconds(0)
    return tomorrow.toISOString()
  }

  const twoWeeksFrom9AM = () => {
    const date = new Date()
    date.setDate(date.getDate() + 14)
    date.setHours(9)
    date.setMinutes(0)
    date.setSeconds(0)
    date.setMilliseconds(0)
    return date.toISOString()
  }

  const [errors, setErrors] = useState<FormErrors>({})
  const {data:editTarget} = useFetch<EmailReminder>(editId == undefined ? '' : `/api/email_reminders/${editId}`)

  const [selectedRecipients, _setSelectedRecipients] = useState<Option[]>([])
  const [recipientCount, setRecipientCount] = useState<number>()
  const setSelectedRecipients = (o:Option[]) => {
    _setSelectedRecipients(o)
    if (o.length > 0) {
      errors.recipients = undefined
      setErrors(errors)
    }
  }

  const [selectedChapters, _setSelectedChapters] = useState<Option[]>([])
  const setSelectedChapters = (o:Option[]) => {
    _setSelectedChapters(o)
    if (o.length > 0) {
      errors.chapters = undefined
      setErrors(errors)
    }
  }

  // get count of recipients
  useEffect(()=>{
    if (selectedChapters.length == 0 || selectedRecipients.length == 0) {
      setRecipientCount(undefined)
      return
    }

    fetch(`/api/email_reminders/recipient_count`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        recipientTypes: selectedRecipients.map(x=>x.value),
        chapterIds: selectedChapters.map(x=>x.value.toString()),
      })
    })
    .then(res=>res.json())
    .then((json)=>{
      setRecipientCount(json.count)
    })
    .catch((err)=>{
      console.error('Failed to fetch recipient count:', err)
      setRecipientCount(undefined)
    })
  }, [selectedRecipients, selectedChapters])

  const [subject, _setSubject] = useState('')
  const setSubject = (s:string) => {
    _setSubject(s)
    if (s && !!s.trim()) {
      errors.subject = undefined
      setErrors(errors)
    }
  }

  const [sendOn, _setSendOn] = useState(tomorrow9AM())

  const setSendOn = (s:string|undefined) => {
    if (!s) return
    _setSendOn(s)
    if (s && !!s.trim()) {
      errors.sendOn = undefined
      setErrors(errors)
    }
  }

  const [htmlBody, _setHtmlBody] = useState('')
  const setHtmlBody = (s:string) => {
    _setHtmlBody(s)
    if (isEmptyHtml(s)) {
      errors.htmlBody = undefined
      setErrors(errors)
    }
  }
  const [saving, setSaving] = useState(false)

  // Recurring email state
  const [isRecurring, setIsRecurring] = useState(false)
  const [frequency, setFrequency] = useState('business_daily')
  const [endDate, _setEndDate] = useState(twoWeeksFrom9AM())
  const setEndDate = (s: string | undefined) => {
    if (!s) return
    _setEndDate(s)
    if (s && !!s.trim()) {
      errors.endDate = undefined
      setErrors(errors)
    }
  }

  // URL inclusion state
  const [selectedUrl, setSelectedUrl] = useState<UrlOption | null>(null)
  
  // Add URL modal state
  const [showAddUrlModal, setShowAddUrlModal] = useState(false)

  const clearForm = () => {
    setSelectedRecipients([])
    setSelectedChapters([])
    setSubject('')
    setSendOn(tomorrow9AM())
    setHtmlBody('')
    setIsRecurring(false)
    setFrequency('business_daily')
    setEndDate(twoWeeksFrom9AM())
    setSelectedUrl(null)
  }

  useEffect(()=>{
    if (!editId || !editTarget) {
      // clear form if editTarget is undefined
      clearForm()
      return
    }

    // update form when editTarget is loaded
    let values = editTarget.recipientTypes.split(',').map(x=>x.trim())
    let selected = recipientOptions.filter(x=>values.includes(x.value))
    setSelectedRecipients(selected)

    values = editTarget.chapterIds.split(',').map(x=>x.trim())
    selected = chapterOptions.filter(x=>values.includes(x.value))
    setSelectedChapters(selected)

    setSubject(editTarget.subject)
    setSendOn(editTarget.sendOn)
    setHtmlBody(editTarget.htmlBody)

    // Load recurring fields
    setIsRecurring(editTarget.isRecurring || false)
    setFrequency(editTarget.frequency || 'business_daily')
    if (editTarget.endDate) setEndDate(editTarget.endDate)

    // Load URL selection
    if (editTarget.includedUrlId) {
      const urlOption = urlOptions.find(u => u.value === editTarget.includedUrlId?.toString())
      setSelectedUrl(urlOption || null)
    } else {
      setSelectedUrl(null)
    }
  }, [editId, editTarget, urlOptions])


  const validate = () => {
    const errors = {} as FormErrors
    const plainText = editorRef.current?.getText()
    let isValid = true
    if (selectedChapters.length == 0) {
      errors.chapters = 'Please select a chapter.'
      isValid = false
    }
    if (selectedRecipients.length == 0) {
      errors.recipients = 'Please select a member group.'
      isValid = false
    }
    if (!subject || !subject.trim()) {
      errors.subject = 'Please provide a subject.'
      isValid = false
    }
    if (!plainText || plainText.trim() === '') {
      errors.htmlBody = 'Please provide a message.'
      isValid = false
    }
    if (!sendOn || !sendOn.trim() || isNaN(new Date(sendOn).getTime())) {
      errors.sendOn = 'Please provide a time.'
      isValid = false
    }

    // Validate end date for recurring reminders
    if (isRecurring) {
      if (!endDate || !endDate.trim() || isNaN(new Date(endDate).getTime())) {
        errors.endDate = 'Please provide an end date.'
        isValid = false
      } else if (new Date(endDate) <= new Date(sendOn)) {
        errors.endDate = 'End date must be after start date.'
        isValid = false
      }
    }

    setErrors(errors)
    return isValid
  }

  const handleSubmit = (e?: { preventDefault?: () => void }) => {
    e?.preventDefault?.()
    if (validate()) save()
  }

  const save = () => {
    setSaving(true)

    const recipientTypes = selectedRecipients.map(x=>x.value).join(',')
    const chapterIds = selectedChapters.map(x=>x.value).join(',')
    const emailReminder = {
        from: defaultFrom,
        recipientTypes,
        chapterIds,
        subject,
        sendOn,
        sentOn: null,
        sentTo: null,
        htmlBody,
        isRecurring,
        frequency: isRecurring ? frequency : null,
        endDate: isRecurring ? endDate : null,
        includedUrlId: selectedUrl ? parseInt(selectedUrl.value) : null,
    }
    if (editId) {
      // update the message
      axios.put(`/api/email_reminders/${editId}`, emailReminder)
      .then(()=>{
        onHide(true, 'updated')
      })
      .catch(err=>{
        // TODO: show retry popup
        console.error(err)
      })
      .finally(()=>{
        setSaving(false)
      })
      return
    }

    axios.post('/api/email_reminders', emailReminder)
    .then(()=>{
      onHide(true, 'created')
    })
    .catch(err=>{
      // TODO: show retry popup
      console.error(err)
      onHide(false)
    })
    .finally(()=>{
      setSaving(false)
    })
  }

  const onCancel = () => {
    setErrors({})
    onHide()
  }
  const fmtCount = (c:number|undefined) => {
    if (c == undefined) return c
    return `(${c.toLocaleString()})`
  }

  const action = editId ? ((editTarget?.sentOn) ? 'Reschedule': 'Update') : 'Schedule'
  const isReadOnly = editTarget?.sentOn ? true : false
  const modalTitle = isReadOnly ? 'Sent Email Reminder' : `${action} Email Reminder`

  return (
    <>
    <PrismDialog
      open={show}
      close-method="user"
      setAriaLabel={modalTitle}
      className={showAddUrlModal ? 'email-modal-dimmed' : ''}
      onPrismClose={onCancel}
    >
      <span slot="header">{modalTitle}</span>
      <div className="email-modal-body">

      <form onSubmit={handleSubmit}>
        <div className="email-modal-field">
          <label className="email-modal-label" id="recipients">Recipients {fmtCount(recipientCount)}</label>
          <PrismTypeahead
            label="Recipients"
            hideLabel
            as="multi"
            options={recipientOptions}
            disabled={isReadOnly}
            invalid={!!errors.recipients}
            placeholder="Search recipients by name…"
            onPrismChange={(e: CustomEvent<{ value?: string }>) => {
              // prism-typeahead reports a multi selection as a delimited string of values.
              const picked = String(e.detail?.value ?? '').split(',').map(v=>v.trim()).filter(Boolean)
              setSelectedRecipients(recipientOptions.filter(o => picked.includes(String(o.value))))
            }}
          />
          <div className="clr:block invalid-feedback">{errors.recipients}</div>
        </div>
        <fieldset className="email-modal-field email-modal-chapters">
          {/* Chapters is a short, browsable list, so a checkbox group beats a typeahead:
              prism-typeahead shows nothing until the user types, and nobody picks a
              chapter they cannot see. */}
          <legend className="email-modal-label" id="chapters">Chapters</legend>
          <div className="email-modal-checkbox-grid">
            {chapterOptions.map(opt => (
              <PrismCheckbox
                key={opt.value}
                label={opt.label}
                checked={selectedChapters.some(c => String(c.value) === String(opt.value))}
                disabled={isReadOnly}
                onPrismChange={(e: CustomEvent<{ checked?: boolean }>) => {
                  const without = selectedChapters.filter(c => String(c.value) !== String(opt.value))
                  setSelectedChapters(e.detail?.checked ? [...without, opt] : without)
                }}
              />
            ))}
          </div>
          <div className="clr:block invalid-feedback">{errors.chapters}</div>
        </fieldset>
        <div className="email-modal-field">
          <PrismInputText
            label="Subject"
            required
            value={subject}
            invalid={!!errors.subject}
            readonly={isReadOnly}
            placeholder='Add a subject...'
            onPrismInput={(e: CustomEvent<{ value?: string }>) => setSubject(e.detail?.value || '')}
          />
          <div className="clr:block invalid-feedback">{errors.subject}</div>
        </div>
        <div className="email-modal-field">
          <label className="email-modal-label">Message</label>
          <MessageEditor
            ref={editorRef}
            className={classNames({'is-invalid':errors.htmlBody})}
            value={htmlBody}
            onChange={setHtmlBody}
            readOnly={isReadOnly}
          />
          <div className="invalid-feedback">{errors.htmlBody}</div>
        </div>
        <div className="email-modal-row clr:mt-4">
          <div className="email-modal-field">
            <label className="email-modal-label">{isRecurring ? 'Start Date' : 'Send On'}</label>
            {isRecurring && <small className="clr:text-secondary-base clr:block clr:mb-1">{frequencyLabels[frequency]}</small>}
            <DateTimePicker value={sendOn} onChange={setSendOn} invalid={!!errors.sendOn} disabled={isReadOnly}/>
            <div className="clr:block invalid-feedback">{errors.sendOn}</div>
          </div>
          <div className="email-modal-field email-modal-field-end">
            <PrismCheckbox
              label="Recurring"
              checked={isRecurring}
              disabled={isReadOnly}
              onPrismChange={(e: CustomEvent<{ checked?: boolean }>) => setIsRecurring(!!e.detail?.checked)}
            />
          </div>
        </div>
        {isRecurring && (
          <div className="email-modal-row clr:mb-4">
            <div className="email-modal-field">
              <PrismSelect
                label="Frequency"
                value={frequency}
                options={frequencyOptions}
                disabled={isReadOnly}
                onPrismChange={(e: CustomEvent<{ value?: string }>) => setFrequency(e.detail?.value || '')}
              />
            </div>
            <div className="email-modal-field">
              <label className="email-modal-label">End Date</label>
              <DateTimePicker value={endDate} onChange={setEndDate} invalid={!!errors.endDate} disabled={isReadOnly}/>
              <div className="clr:block invalid-feedback">{errors.endDate}</div>
            </div>
          </div>
        )}
        <div className="email-modal-field clr:mt-4">
          <PrismSelect
            label="Include URL (optional)"
            value={selectedUrl?.value ?? ''}
            disabled={isReadOnly}
            options={[
              { value: '', display: 'Select a URL to include...' },
              { value: '__create_new__', display: '+ Create New URL' },
              ...urlOptions.map(o => ({ value: o.value, display: o.label })),
            ]}
            onPrismChange={(e: CustomEvent<{ value?: string }>) => {
              const picked = String(e.detail?.value ?? '')
              if (picked === '__create_new__') {
                setShowAddUrlModal(true)
              } else {
                setSelectedUrl(urlOptions.find(o => String(o.value) === picked) ?? null)
              }
            }}
          />
          {selectedUrl && (
            <div className="clr:mt-2 clr:p-2 email-modal-url-preview">
              <small className="clr:text-secondary-base">URL Preview:</small>
              <br />
              <a href={selectedUrl.url} target="_blank" rel="noopener noreferrer">
                {selectedUrl.url}
              </a>
            </div>
          )}
        </div>
      </form>
      </div>
      <div slot="footer" className="email-modal-footer" data-readonly={isReadOnly ? 'true' : 'false'}>
        {!isReadOnly && <PrismButton tone="secondary" display="outline" onPrismClick={onCancel}>Cancel</PrismButton>}
        {isReadOnly ? (
          <PrismButton tone="secondary" display="outline" onPrismClick={onCancel}>Close</PrismButton>
        ) : (
          <div className="email-modal-footer-actions">
            {editId && onDelete && (
              <PrismButton
                tone="danger"
                disabled={saving}
                onPrismClick={() => setShowDeleteModal(true)}
              >
                Remove
              </PrismButton>
            )}
            <PrismButton
              tone="success"
              disabled={saving}
              onPrismClick={handleSubmit}
            >
              {`${action } Reminder`}
            </PrismButton>
          </div>
        )}
      </div>
    </PrismDialog>

    {/* Delete Confirmation Modal */}
    <PrismDialog
      open={showDeleteModal}
      close-method="user"
      setAriaLabel="Confirm Delete"
      onPrismClose={() => setShowDeleteModal(false)}
    >
      <span slot="header">Confirm Delete</span>
      <div>
        <p>Are you sure you want to delete this email reminder?</p>
        <div className="email-modal-warning" role="alert">
          <small>
            This action cannot be undone. The email reminder will be permanently deleted.
          </small>
        </div>
      </div>
      <PrismButton slot="footer" tone="secondary" display="outline" onPrismClick={() => setShowDeleteModal(false)}>
        Cancel
      </PrismButton>
      <PrismButton
        slot="footer"
        tone="danger"
        onPrismClick={() => {
          if (editId && onDelete) {
            onDelete(editId)
            setShowDeleteModal(false)
            onHide()
          }
        }}
      >
        Delete
      </PrismButton>
    </PrismDialog>

    {/* Add URL Modal */}
    <AddUrlModal
      show={showAddUrlModal}
      onHide={() => setShowAddUrlModal(false)}
      onUrlCreated={(newUrl) => {
        // Select the newly created URL
        setSelectedUrl({
          value: newUrl.id.toString(),
          label: newUrl.name,
          url: newUrl.url
        })
        // Refresh the parent's URL list
        onUrlCreated()
      }}
    />
    </>
  )
}

export default EmailModal